import React, { useState } from 'react';
import { Appointment } from '../services/mobileAdminApi';

interface MobileAdminCardProps {
  appointment: Appointment;
  onConfirm: (id: number) => void;
  onReject: (id: number) => void;
  isLoading?: boolean;
}

const MobileAdminCard: React.FC<MobileAdminCardProps> = ({
  appointment,
  onConfirm,
  onReject,
  isLoading = false,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
      case 'confirmed':
        return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'completed':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'cancelled':
        return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'pending':
        return '⏳ Beklemede';
      case 'confirmed':
        return '✅ Onaylandı';
      case 'completed':
        return '🎉 Tamamlandı';
      case 'cancelled':
        return '❌ İptal Edildi';
      default:
        return status;
    }
  };

  const getStatusEmoji = (status: string): string => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'confirmed':
        return '✅';
      case 'completed':
        return '🎉';
      case 'cancelled':
        return '❌';
      default:
        return '❓';
    }
  };

  const formatTime = (time: string): string => {
    return time.substring(0, 5); // HH:MM
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const servicesList = appointment.serviceNames.length > 0
    ? appointment.serviceNames.join(', ')
    : 'Hizmet belirtilmemiş';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-4 border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:shadow-lg">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">{getStatusEmoji(appointment.status)}</span>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {appointment.customerName}
            </h3>
          </div>
          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
            {getStatusText(appointment.status)}
          </span>
        </div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors p-2 -mr-2 -mt-2"
          aria-label={showDetails ? 'Hide details' : 'Show details'}
        >
          <svg
            className={`w-6 h-6 transform transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Main Info */}
      <div className="space-y-2 mb-4 text-sm">
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <span className="text-base">⏰</span>
          <span className="font-medium">{formatTime(appointment.time)}</span>
        </div>

        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <span className="text-base">📱</span>
          <a
            href={`tel:${appointment.customerPhone}`}
            className="font-medium hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            {appointment.customerPhone}
          </a>
        </div>

        <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
          <span className="text-base mt-0.5">💅</span>
          <span className="flex-1 font-medium">{servicesList}</span>
        </div>

        {appointment.teamMember && (
          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <span className="text-base">👩‍💼</span>
            <span className="font-medium">{appointment.teamMember.name}</span>
          </div>
        )}
      </div>

      {/* Details (Collapsible) */}
      {showDetails && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mb-4 space-y-2 text-sm">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <span className="text-base">📧</span>
            <a
              href={`mailto:${appointment.customerEmail}`}
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors break-all"
            >
              {appointment.customerEmail}
            </a>
          </div>

          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <span className="text-base">📅</span>
            <span>{formatDate(appointment.date)}</span>
          </div>

          {appointment.totalPrice && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <span className="text-base">💰</span>
              <span className="font-semibold">{appointment.totalPrice} TL</span>
            </div>
          )}

          {appointment.notes && (
            <div className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
              <span className="text-base mt-0.5">📝</span>
              <span className="flex-1">{appointment.notes}</span>
            </div>
          )}

          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-500 text-xs">
            <span className="text-base">🆔</span>
            <span>Randevu #{appointment.id}</span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {appointment.status === 'pending' && (
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => onConfirm(appointment.id)}
            disabled={isLoading}
            className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 min-h-[44px] active:scale-95 transform"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <span>✅</span>
                <span>Onayla</span>
              </>
            )}
          </button>
          <button
            onClick={() => onReject(appointment.id)}
            disabled={isLoading}
            className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 min-h-[44px] active:scale-95 transform"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <span>❌</span>
                <span>İptal</span>
              </>
            )}
          </button>
        </div>
      )}

      {appointment.status === 'confirmed' && (
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => onReject(appointment.id)}
            disabled={isLoading}
            className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 min-h-[44px] active:scale-95 transform"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <span>❌</span>
                <span>İptal Et</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default MobileAdminCard;


